import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  InstantaneousBufferDataOut,
  LoadProfileCommandEvent,
  PageInfo,
} from '../../../api/api-reference';
import { generateFileNameAppendix } from '../../../helper/download-filename';
import { UnitConverter } from '../../../pipes/unit-conversion';
import { FileService } from '../../../services/file.service';
import { TableScrollService } from '../../../services/table-scroll.service';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { processDateRange } from '../../../helper/date-utils';
import { ReadsType } from '../scheduled.module';

@Component({
  selector: 'app-scheduled-instantaneous-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    AngularSvgIconModule,
    TranslateModule,
    PaginationComponent,
    UnitConverter,
    DatepickerComponent
  ],
  templateUrl: './scheduled-instantaneous-details.component.html',
  styleUrl: './scheduled-instantaneous-details.component.scss',
})
export class ScheduledInstantaneousDetailsComponent implements AfterViewInit {
  ReadsType = ReadsType
  instantaneousGet: LoadProfileCommandEvent = new LoadProfileCommandEvent();
  readOnMeterTime: Date | undefined;
  commandId?: string;
  eventId?: string;
  data: InstantaneousBufferDataOut[] = [];
  isDatepickerOpen = false;
  isSelectOptionOpen = false;
  selectedReadOption: ReadsType = ReadsType.Processed;
  
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  public pageSize: number = 10;
  public pageNumber: number = 1;

  requestData = new LoadProfileCommandEvent({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    meterId: '',
    startDate: undefined,
    endDate: undefined,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private tableScrollService: TableScrollService,
    private fileService: FileService,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.requestData.meterId = params['meterId'];
      if (params['startDate'] && params['endDate']) {
        this.requestData.startDate = new Date(params['startDate']);
        this.requestData.endDate = new Date(params['endDate']);
        this.selectedDate = params['selectedDate'];
      }
    });

    this.reloadTable(1);
  }

  @ViewChild('tableContainer') tableContainer!: ElementRef;

  ngAfterViewInit(): void {
    this.initializeTableEvents();
  }

  private initializeTableEvents(): void {
    const tableContainer = this.tableContainer.nativeElement;
    // Drag events
    tableContainer.addEventListener('mousedown', (event: MouseEvent) =>
      this.tableScrollService.startDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mousemove', (event: MouseEvent) =>
      this.tableScrollService.onDrag(event, tableContainer),
    );
    tableContainer.addEventListener('mouseup', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    tableContainer.addEventListener('mouseleave', () =>
      this.tableScrollService.stopDrag(tableContainer),
    );
    // Wheel event for horizontal scroll
    tableContainer.addEventListener('wheel', (event: WheelEvent) =>
      this.tableScrollService.handleHorizontalScroll(event, tableContainer),
    );
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.reloadTable(1);
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    const request$ = this.selectedReadOption == ReadsType.Raw
    ? this.client.getRawInstantaneousObject(this.requestData)
    : this.client.getProcessedInstantaneousObject(this.requestData);

    request$.subscribe({
      next: (response) => {
        this.data = response.data!;
        this.numberOfCommands = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
        this.currentPage = page || 1;
        if (response.count! == 0) {
          this.toastr.warning("Readings don't exist");
        }
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const request$ = this.selectedReadOption == ReadsType.Raw
        ? this.client.downloadRawInstantaneousObjectFile(this.requestData)
        : this.client.downloadProcessedInstantaneousObjectFile(this.requestData);

    request$.subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `InstantaneousObject${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  toggleSelectOption(): void {
    this.isSelectOptionOpen = !this.isSelectOptionOpen;
  }

  closeSelectOption(): void {
    this.isSelectOptionOpen = false;
  }

  selectReadOption(option: ReadsType): void {
    this.selectedReadOption = option;
  }

  applyReadOption(): void {
    this.closeSelectOption();
    this.pageChange(1);
  }
}